import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { injectIntl, FormattedHTMLMessage } from 'react-intl'
import { Field, reduxForm } from 'redux-form'
import FormattedMessage from '../../FormattedMessage/FormattedMessage'
import { renderField } from '../../../utilities/reduxForm.util'
import { required, alphaNumeric } from '../../../utilities/validators'
import Loader from '../../Loader/Loader'

import './ClaimRewardCouponForm.scss'

class ClaimRewardCouponForm extends Component {
  constructor(props) {
    super(props)

    this.state = {
      modal: false,
      couponClaimErrorState: false,
    }

    // Reference for focus management
    this.formRef = React.createRef()
  }

  static getDerivedStateFromProps(nextProps, state) {
    const { couponClaimErrorState, modal } = state
    if (nextProps.couponClaimError !== couponClaimErrorState && !couponClaimErrorState && !modal) {
      return {
        couponClaimErrorState: nextProps.couponClaimError,
      }
    }
    return null
  }

  componentDidUpdate(prevProps) {
    const { couponClaimLoading } = this.props
    const { couponClaimErrorState, modal } = this.state

    // Open modal when error occurs
    if (!modal && prevProps.couponClaimError !== couponClaimErrorState) {
      this.openModal()
    }

    // Focus management
    if (!prevProps.couponClaimLoading && couponClaimLoading) {
      // Save last focus position before loading starts
      this.lastFocusedElement = document.activeElement
    }

    if (prevProps.couponClaimLoading && !couponClaimLoading) {
      // Restore focus after loading completes
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus()
      }
    }
  }

  openModal = () => {
    this.setState({ modal: true })
  }

  closeModal = () => {
    this.setState({ modal: false, couponClaimErrorState: false })

    // Return focus to the form after closing modal
    if (this.formRef.current) {
      setTimeout(() => {
        const focusableElement = this.formRef.current.querySelector('input, button')
        if (focusableElement) {
          focusableElement.focus()
        }
      }, 50)
    }
  }

  render() {
    const { modal } = this.state
    const {
      couponClaimLoading,
      handleSubmit,
      submitFailed,
      parentComponent,
      couponClaimError,
      intl,
    } = this.props

    const couponClaimErrorId =
      typeof couponClaimError === 'string' && couponClaimError.length > 0 ? couponClaimError : null

    const label = intl.formatMessage({ id: 'page.redemptionPage.text.labelEnterCouponCode' })

    const isDefaultSupportLinkValid = true
    const isAMD50FAQLinkValid = true

    // Determine the support URL based on parent component
    const supportUrl = parentComponent === 'AMD50' ? '/amd50/faq' : '/support'
    const supportLinkId =
      parentComponent === 'AMD50' ? 'amd50.error.footer' : 'error.global.footerLink'
    const supportLinkValid =
      parentComponent === 'AMD50' ? isAMD50FAQLinkValid : isDefaultSupportLinkValid

    return (
      <form
        onSubmit={handleSubmit}
        method="post"
        ref={this.formRef}
        noValidate
        aria-labelledby="coupon-form-title"
      >
        <div className="sr-only" id="coupon-form-title">
          <FormattedMessage id="page.redemptionPage.title.heroTitle" />
        </div>

        <Modal
          isOpen={modal}
          toggle={this.closeModal}
          size="lg"
          returnFocusAfterClose={false}
          aria-labelledby="error-modal-title"
          aria-describedby="error-modal-description"
        >
          <ModalHeader toggle={this.closeModal} id="error-modal-title">
            <FormattedMessage id="global.label.error" />
          </ModalHeader>
          <ModalBody id="error-modal-description">
            {couponClaimErrorId && (
              <div>
                <FormattedHTMLMessage id={couponClaimErrorId} />
              </div>
            )}

            {/* Conditional support link section */}
            {parentComponent && (
              <div className="mt-4">
                <small>
                  {parentComponent === 'default' && (
                    <FormattedMessage id="error.global.footerText" />
                  )}
                  <br />
                  {supportLinkValid ? (
                    <a
                      href={supportUrl}
                      aria-label={`${intl.formatMessage({
                        id: supportLinkId,
                      })} - ${intl.formatMessage({ id: 'global.label.helpAndSupport' })}`}
                    >
                      <FormattedHTMLMessage id={supportLinkId} />
                    </a>
                  ) : (
                    <span className="text-muted">
                      <FormattedHTMLMessage id={supportLinkId} />
                      <span className="sr-only">
                        {' '}
                        {intl.formatMessage({ id: 'global.label.linkUnavailable' })}
                      </span>
                    </span>
                  )}
                </small>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <div className="w-100">
              <div className="row">
                <div className="col">
                  <Button
                    block
                    outline
                    color="danger"
                    onClick={this.closeModal}
                    aria-label={intl.formatMessage({ id: 'global.label.closeErrorMessage' })}
                  >
                    <FormattedMessage id="global.label.popoverClose" />
                  </Button>
                </div>
              </div>
            </div>
          </ModalFooter>
        </Modal>

        <div className="form-group">
          <Field
            component={renderField}
            validate={[required, alphaNumeric]}
            type="text"
            inputType="input"
            name="couponCode"
            label={label}
            aria-required="true"
            required
            autoComplete="off"
          />
        </div>

        {submitFailed && (
          <div
            className="form-field-validation-error-message mb-2"
            role="alert"
            aria-live="assertive"
          >
            <p>
              <FormattedMessage id="error.global.footerText" />
              <br />
              {isDefaultSupportLinkValid ? (
                <a
                  href="/support"
                  aria-label={`${intl.formatMessage({
                    id: 'error.global.footerLink',
                  })} - ${intl.formatMessage({ id: 'global.label.helpAndSupport' })}`}
                >
                  <FormattedHTMLMessage id="error.global.footerLink" />
                </a>
              ) : (
                <span className="text-muted">
                  <FormattedHTMLMessage id="error.global.footerLink" />
                  <span className="sr-only">
                    {' '}
                    {intl.formatMessage({ id: 'global.label.linkUnavailable' })}
                  </span>
                </span>
              )}
            </p>
          </div>
        )}

        {couponClaimLoading ? (
          <div aria-live="polite">
            <Loader aria-label={intl.formatMessage({ id: 'global.label.loading' })} />
          </div>
        ) : (
          <Button
            type="submit"
            outline
            color="dark"
            block
            name="claimRewardCouponFormSubmitBtn"
            aria-label={intl.formatMessage({ id: 'page.redemptionPage.button.submitCouponCode' })}
          >
            <FormattedMessage id="page.redemptionPage.button.submitCouponCode" />
          </Button>
        )}
      </form>
    )
  }
}

ClaimRewardCouponForm.propTypes = {
  couponClaimLoading: PropTypes.bool,
  couponClaimError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  handleSubmit: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool,
  parentComponent: PropTypes.oneOf(['default', 'AMD50']),
  intl: PropTypes.object.isRequired,
}

ClaimRewardCouponForm.defaultProps = {
  parentComponent: 'default',
}

const mapStateToProps = (state, ownProps) => ({
  couponClaimLoading: state.claimReducers.couponClaimLoading,
  couponClaimError:
    (ownProps.parentComponent === 'default' && state.claimReducers.couponClaimError) ||
    (ownProps.parentComponent === 'AMD50' && state.amd50Reducer.couponClaimError),
})

export default injectIntl(
  connect(mapStateToProps)(
    reduxForm({
      form: 'claimRewardCouponForm',
    })(ClaimRewardCouponForm)
  )
)
